import React, { useState, useEffect } from 'react';
import { MessageSquarePlus, Clock, Share2, Users, X, Check } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useThemeStore } from '../stores/themeStore';
import { collection, query, where, getDocs, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { toast } from 'react-hot-toast';

interface Note {
  id: string;
  content: string;
  createdAt: Date;
  author: string;
  sharedWith?: string[];
}

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  photoURL?: string;
}

interface NotesSectionProps {
  notes: Note[];
  onAddNote: (content: string) => void;
  parentId?: string;
  parentType?: 'company' | 'agency' | 'beneficiaire';
}

function NotesSection({ notes: initialNotes, onAddNote, parentId, parentType }: NotesSectionProps) {
  const [notes, setNotes] = useState<Note[]>(initialNotes);
  const [newNote, setNewNote] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { darkMode } = useThemeStore();

  useEffect(() => {
    setNotes(initialNotes);
  }, [initialNotes]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newNote.trim()) {
      const note: Note = {
        id: Date.now().toString(),
        content: newNote.trim(),
        createdAt: new Date(),
        author: auth.currentUser?.displayName || "Utilisateur"
      };
      
      setNotes(prevNotes => [note, ...prevNotes]);
      onAddNote(newNote);
      setNewNote('');
    }
  };

  const handleShareNote = (note: Note) => {
    setSelectedNote(note);
    setShowShareModal(true);
    loadUsers();
  };

  const loadUsers = async () => {
    if (!auth.currentUser) return;
    
    setIsLoading(true);
    try {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      
      const usersList: User[] = [];
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Ne pas inclure l'utilisateur actuel dans la liste
        if (doc.id !== auth.currentUser?.uid) {
          usersList.push({
            id: doc.id,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            role: data.role,
            photoURL: data.photoURL
          });
        }
      });
      
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareWithUser = async (user: User) => {
    if (!selectedNote || !parentId || !parentType || !auth.currentUser) return;
    
    try {
      // Mettre à jour le document parent pour ajouter l'utilisateur à la liste de partage de la note
      const parentRef = doc(db, parentType + 's', parentId);
      
      // Trouver l'index de la note dans le tableau
      const noteIndex = notes.findIndex(n => n.id === selectedNote.id);
      
      if (noteIndex === -1) {
        toast.error('Note introuvable');
        return;
      }
      
      // Créer un tableau de notes mis à jour
      const updatedNotes = [...notes];
      
      // Ajouter ou initialiser le tableau sharedWith pour cette note
      if (!updatedNotes[noteIndex].sharedWith) {
        updatedNotes[noteIndex].sharedWith = [];
      }
      
      // Ajouter l'utilisateur s'il n'est pas déjà dans la liste
      if (!updatedNotes[noteIndex].sharedWith?.includes(user.id)) {
        updatedNotes[noteIndex].sharedWith?.push(user.id);
      }
      
      // Mettre à jour Firestore
      await updateDoc(parentRef, {
        [`notes.${noteIndex}.sharedWith`]: arrayUnion(user.id)
      });
      
      // Mettre à jour l'état local
      setNotes(updatedNotes);
      
      toast.success(`Note partagée avec ${user.email}`);
    } catch (error) {
      console.error('Error sharing note:', error);
      toast.error('Erreur lors du partage de la note');
    }
  };

  const handleRemoveShare = async (user: User) => {
    if (!selectedNote || !parentId || !parentType || !auth.currentUser) return;
    
    try {
      // Mettre à jour le document parent pour retirer l'utilisateur de la liste de partage de la note
      const parentRef = doc(db, parentType + 's', parentId);
      
      // Trouver l'index de la note dans le tableau
      const noteIndex = notes.findIndex(n => n.id === selectedNote.id);
      
      if (noteIndex === -1) {
        toast.error('Note introuvable');
        return;
      }
      
      // Créer un tableau de notes mis à jour
      const updatedNotes = [...notes];
      
      // Retirer l'utilisateur de la liste de partage
      if (updatedNotes[noteIndex].sharedWith) {
        updatedNotes[noteIndex].sharedWith = updatedNotes[noteIndex].sharedWith.filter(id => id !== user.id);
      }
      
      // Mettre à jour Firestore
      await updateDoc(parentRef, {
        [`notes.${noteIndex}.sharedWith`]: arrayRemove(user.id)
      });
      
      // Mettre à jour l'état local
      setNotes(updatedNotes);
      
      toast.success(`Partage retiré pour ${user.email}`);
    } catch (error) {
      console.error('Error removing share:', error);
      toast.error('Erreur lors du retrait du partage');
    }
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email.split('@')[0];
  };

  const getUserInitials = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      (user.firstName && user.firstName.toLowerCase().includes(searchLower)) ||
      (user.lastName && user.lastName.toLowerCase().includes(searchLower)) ||
      (user.role && user.role.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm`}>
      <div className="p-6">
        <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4 flex items-center`}>
          <MessageSquarePlus className="w-5 h-5 mr-2 text-blue-600" />
          Notes de suivi
        </h2>

        <form onSubmit={handleSubmit} className="mb-6">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Ajouter une note..."
            className={`w-full p-3 border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
            rows={3}
          />
          <div className="mt-2 flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!newNote.trim()}
            >
              Ajouter la note
            </button>
          </div>
        </form>

        <div className="space-y-4">
          {notes.map((note) => (
            <div key={note.id} className={`p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
              <div className="flex justify-between items-start">
                <p className={`${darkMode ? 'text-white' : 'text-gray-800'} whitespace-pre-wrap`}>{note.content}</p>
                {parentId && parentType && (
                  <button
                    onClick={() => handleShareNote(note)}
                    className={`ml-2 p-1.5 ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'} rounded-lg transition-colors`}
                    title="Partager cette note"
                  >
                    <Share2 className={`w-4 h-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                  </button>
                )}
              </div>
              <div className={`mt-3 flex items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <Clock className="w-4 h-4 mr-1" />
                <span>
                  {format(note.createdAt, "d MMMM yyyy 'à' HH:mm", { locale: fr })}
                </span>
                <span className="mx-2">•</span>
                <span>{note.author}</span>
                
                {note.sharedWith && note.sharedWith.length > 0 && (
                  <div className="ml-2 flex items-center">
                    <Users className="w-4 h-4 mr-1 text-blue-500" />
                    <span className="text-blue-500">{note.sharedWith.length}</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {notes.length === 0 && (
            <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'} py-4`}>
              Aucune note pour le moment
            </p>
          )}
        </div>
      </div>

      {/* Modal de partage */}
      {showShareModal && selectedNote && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-xl font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Share2 className="w-5 h-5 mr-2 text-blue-600" />
                  Partager la note
                </h2>
                <button onClick={() => setShowShareModal(false)} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4">
                <div className={`p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg mb-4`}>
                  <p className={`${darkMode ? 'text-white' : 'text-gray-800'} whitespace-pre-wrap`}>{selectedNote.content}</p>
                </div>
              </div>

              <div className="mb-4">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Partagée avec ({selectedNote.sharedWith?.length || 0})
                </label>
                
                {selectedNote.sharedWith && selectedNote.sharedWith.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedNote.sharedWith.map(userId => {
                      const user = users.find(u => u.id === userId);
                      if (!user) return null;
                      
                      return (
                        <div 
                          key={user.id} 
                          className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                        >
                          <div className="flex items-center gap-3">
                            {user.photoURL ? (
                              <img 
                                src={user.photoURL} 
                                alt={getUserDisplayName(user)} 
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                {getUserInitials(user)}
                              </div>
                            )}
                            <div>
                              <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {getUserDisplayName(user)}
                              </div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {user.email}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveShare(user)}
                            className={`px-3 py-1 text-xs rounded-lg ${darkMode ? 'bg-red-900 text-red-300 hover:bg-red-800' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                          >
                            Retirer
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Cette note n'est partagée avec personne pour le moment.
                  </p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Partager avec d'autres utilisateurs
                </label>
                <input
                  type="text"
                  placeholder="Rechercher un utilisateur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5 mb-4`}
                />

                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {isLoading ? (
                    <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Chargement des utilisateurs...
                    </div>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers
                      .filter(user => !selectedNote.sharedWith?.includes(user.id))
                      .map(user => (
                        <div 
                          key={user.id} 
                          className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}
                        >
                          <div className="flex items-center gap-3">
                            {user.photoURL ? (
                              <img 
                                src={user.photoURL} 
                                alt={getUserDisplayName(user)} 
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                {getUserInitials(user)}
                              </div>
                            )}
                            <div>
                              <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {getUserDisplayName(user)}
                              </div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {user.email}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleShareWithUser(user)}
                            className={`flex items-center px-3 py-1 text-xs rounded-lg ${darkMode ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                          >
                            <Share2 className="w-3 h-3 mr-1" />
                            Partager
                          </button>
                        </div>
                      ))
                  ) : (
                    <p className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {searchTerm ? 'Aucun utilisateur trouvé' : 'Commencez à taper pour rechercher des utilisateurs'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotesSection;